/**
 * Simulator page — Supabase-native, no Telerivet.
 * Creates synthetic contacts directly in Supabase and runs agents via agentfactory,
 * which writes messages + runs the eval pipeline automatically.
 */

import { useState, useEffect } from "react"
import { useSearchParams, useNavigate } from "react-router-dom"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Button } from "@/components/ui/button"
import { MessageSquare, Zap, ArrowLeft, User } from "lucide-react"
import { usePage } from "@/components/page/hooks"
import { useTeamStore } from "@/lib/hooks/useTeam"
import { supabase } from "@/lib/data"
import { BulkAndLoopSimulator, MultiSimulator, SingleSimulator } from "./components"
import { FormPanel } from "@/pages/dev/moderation/components"

export default function SimulatorPage() {
  const { team } = usePage()
  const { selectedTeam } = useTeamStore()
  const teamId = selectedTeam?.id || team?.id

  // When opened from a moderation conversation (e.g. via the "Simulator"
  // button), these params seed a dedicated "continue as the user" view.
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const continueContactId = searchParams.get("contact") || ""
  const continueAgentId = searchParams.get("agent") || ""

  const clearContinue = () => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      next.delete("contact")
      next.delete("agent")
      return next
    })
  }

  // Go back to the originating moderation conversation.
  const backToModeration = () => {
    navigate(`/moderation?contact=${encodeURIComponent(continueContactId)}`)
  }

  // Resolve the contact's display name for the continue-view header.
  const [continueContactName, setContinueContactName] = useState<string | null>(null)
  useEffect(() => {
    if (!continueContactId) { setContinueContactName(null); return }
    let cancelled = false
    supabase
      .from("contacts")
      .select("name")
      .eq("id", continueContactId)
      .single()
      .then(({ data }) => {
        if (!cancelled) setContinueContactName((data as any)?.name || null)
      })
    return () => { cancelled = true }
  }, [continueContactId])

  const [agentsData, setAgentsData] = useState<{ id: number | string; title: string }[]>([])
  const [loadingAgents, setLoadingAgents] = useState(true)

  useEffect(() => {
    if (!teamId) return
    setLoadingAgents(true)
    supabase
      .from("agents")
      .select("id, title, status")
      .eq("team_id", teamId)
      .then(({ data }) => {
        const visibleAgents = (data || []).filter((agent) => (agent.status || "active") !== "archived")
        setAgentsData(visibleAgents.map(({ id, title }) => ({ id, title })))
        setLoadingAgents(false)
      })
  }, [teamId])

  // Dedicated "continue this conversation as the user" view, opened from
  // moderation. Loads the existing synthetic contact's thread and lets the user
  // keep chatting with the agent as the contact.
  if (continueContactId) {
    const continueHeader = (
      <>
        <Button variant="ghost" size="sm" className="h-7 text-xs gap-1.5 shrink-0" onClick={backToModeration}>
          <ArrowLeft className="h-3 w-3" />
          Back to moderation
        </Button>
        <Button variant="ghost" size="sm" className="h-7 text-xs gap-1.5 shrink-0" onClick={clearContinue}>
          <ArrowLeft className="h-3 w-3" />
          Back to simulator
        </Button>

        <div className="flex items-center gap-1.5 ml-1 min-w-0">
          <User className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <span className="text-sm font-semibold truncate" title={continueContactName || continueContactId}>
            {continueContactName || "Simulated user"}
          </span>
        </div>

        <span className="text-xs text-muted-foreground ml-auto mr-2 shrink-0 hidden md:inline">
          You are acting as the user
        </span>
      </>
    )

    return (
      <div className="flex h-full overflow-hidden gap-2 p-2">
        <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
          <SingleSimulator
            agentId={continueAgentId || undefined}
            teamId={teamId}
            initialContactId={continueContactId}
            headerSlot={continueHeader}
          />
        </div>
        <div className="w-85 shrink-0 flex flex-col">
          <FormPanel contactId={continueContactId} teamId={teamId} agentId={continueAgentId || undefined} pollMs={6000} />
        </div>
      </div>
    )
  }

  return (
    <Tabs defaultValue="chat" className="flex flex-col h-full overflow-hidden">
      <div className="shrink-0 px-3 pt-2 pb-2 border-b">
        <div className="flex items-center justify-end">
          <TabsList className="h-8">
            <TabsTrigger value="chat" className="text-xs h-7 px-3">
              <MessageSquare className="h-3 w-3 mr-1.5" />
              Chat
            </TabsTrigger>
            <TabsTrigger value="simulate" className="text-xs h-7 px-3">
              <Zap className="h-3 w-3 mr-1.5" />
              Simulate
            </TabsTrigger>
          </TabsList>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-hidden">
        <TabsContent value="chat" className="h-full m-0">
          <MultiSimulator
            teamId={teamId || ""}
            agents={agentsData}
            loadingAgents={loadingAgents}
          />
        </TabsContent>

        <TabsContent value="simulate" className="h-full m-0 overflow-auto">
          <BulkAndLoopSimulator teamId={teamId || ""} agents={agentsData} loadingAgents={loadingAgents} />
        </TabsContent>
      </div>
    </Tabs>
  )
}
